/**
 * Slack Notifications Utility
 * Session-based tracking with threaded messages for user journey
 *
 * ENHANCED SESSION TRACKING:
 *
 * This system now handles complex user navigation scenarios:
 *
 * 1. **TSP → Back → Different TSP**: When a user tries one TSP, goes back to the list,
 *    and selects another, the system creates a new "attempt" within the same session.
 *    This is tracked as "Attempt #2", "Attempt #3", etc.
 *
 * 2. **Success → Add Another TSP**: When a user completes onboarding and clicks
 *    "Add Another Provider", the system starts a new attempt for the additional TSP.
 *
 * 3. **Session Management**: Sessions are keyed by `serviceId-fleetCode` and include:
 *    - currentAttempt: Tracks multiple TSP attempts within the session
 *    - completedTSPs: Set of successfully onboarded TSPs
 *    - isCompleted: Whether the current attempt was completed
 *    - activities: Set of tracked activities for deduplication
 *
 * 4. **Attempt Separation**: Each new attempt gets a visual separator in Slack:
 *    "🔄 **--- Attempt #2 ---**" to clearly distinguish different TSP attempts.
 *
 * 5. **Smart Deduplication**: Activities are deduplicated within each attempt,
 *    but the same activity can be tracked across different attempts.
 */
import { JWTContextValue } from '../contexts/JWTProvider';

export interface SlackNotificationContext {
  serviceName?: string;
  serviceId?: number | null;
  fleetCode?: string | null;
  token?: string | null;
  tsp?: string;
  userId?: string;
  sessionId?: string;
  timestamp?: string;
}

export enum SlackEventType {
  // Session start (creates new message)
  ONBOARDING_STARTED = 'onboarding_started',

  // Activity events (threaded replies)
  PAGE_VIEW = 'page_view',
  TSP_SELECTED = 'tsp_selected',
  TSP_REMOVED = 'tsp_removed',
  CREDENTIALS_ENTERED = 'credentials_entered',
  ERROR_ENCOUNTERED = 'error_encountered',
  HELP_REQUESTED = 'help_requested',
  SKIP_INITIATED = 'skip_initiated',
  RETRY_ATTEMPTED = 'retry_attempted',
  BACK_NAVIGATION = 'back_navigation',
  TERMS_CLICKED = 'terms_clicked',
  PRIVACY_CLICKED = 'privacy_clicked',

  // Session end events (threaded replies)
  ONBOARDING_COMPLETED = 'onboarding_completed',
  ONBOARDING_SKIPPED = 'onboarding_skipped',
  ONBOARDING_FAILED = 'onboarding_failed',
}

/**
 * Configuration for Slack notifications
 */
interface SlackConfig {
  channel?: string;
  enabled: boolean;
  environment: 'development' | 'staging' | 'production';
}

/**
 * Detect environment based on hostname
 */
function detectEnvironment(): 'development' | 'staging' | 'production' {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('localhost')
    ) {
      return 'development';
    }

    if (
      hostname.includes('developer-staging.axleapi.com') ||
      hostname.includes('staging')
    ) {
      return 'staging';
    }

    if (
      hostname.includes('developer.axleapi.com') ||
      hostname.includes('axleapi.com')
    ) {
      return 'production';
    }
  }

  return 'development';
}

/**
 * Get Slack configuration based on environment
 */
function getSlackConfig(): SlackConfig {
  const environment = detectEnvironment();

  let channel: string;
  switch (environment) {
    case 'development':
      channel = '#bot-axle-link-dev';
      break;
    case 'staging':
      channel = '#bot-axle-link-stage';
      break;
    case 'production':
      channel = '#bot-axle-link-prod';
      break;
    default:
      channel = '#bot-axle-link-dev';
  }

  return {
    channel,
    enabled: true,
    environment,
  };
}

/**
 * Session tracking for threaded messages
 */
class SessionTracker {
  private sessions = new Map<
    string,
    {
      messageId?: string;
      lastActivity: string;
      activities: Set<string>;
      startedAt: number;
      currentAttempt: number;
      completedTSPs: Set<string>;
      isCompleted: boolean;
    }
  >();
  private pendingStarts = new Set<string>();
  private readonly STORAGE_KEY = 'slack_sessions';
  private readonly PENDING_KEY = 'slack_pending';

  constructor() {
    // Restore sessions from localStorage on initialization
    this.restoreFromStorage();
  }

  /**
   * Save sessions to localStorage
   */
  private saveToStorage(): void {
    try {
      const sessionsData: Record<string, any> = {};

      this.sessions.forEach((session, key) => {
        sessionsData[key] = {
          ...session,
          activities: Array.from(session.activities),
          completedTSPs: Array.from(session.completedTSPs),
        };
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionsData));
      localStorage.setItem(
        this.PENDING_KEY,
        JSON.stringify(Array.from(this.pendingStarts))
      );
    } catch (error) {
      console.warn('[Slack] Failed to save sessions to localStorage:', error);
    }
  }

  /**
   * Restore sessions from localStorage
   */
  private restoreFromStorage(): void {
    try {
      const sessionsData = localStorage.getItem(this.STORAGE_KEY);
      const pendingData = localStorage.getItem(this.PENDING_KEY);

      if (sessionsData) {
        const parsed = JSON.parse(sessionsData);
        Object.entries(parsed).forEach(([key, data]: [string, any]) => {
          this.sessions.set(key, {
            ...data,
            activities: new Set(data.activities || []),
            completedTSPs: new Set(data.completedTSPs || []),
          });
        });
      }

      if (pendingData) {
        const pendingArray = JSON.parse(pendingData);
        this.pendingStarts = new Set(pendingArray);
      }

      // Only log if sessions were actually restored
      if (this.sessions.size > 0 || this.pendingStarts.size > 0) {
        console.log('[Slack] Restored sessions from localStorage:', {
          sessionCount: this.sessions.size,
          pendingCount: this.pendingStarts.size,
        });
      }
    } catch (error) {
      console.warn(
        '[Slack] Failed to restore sessions from localStorage:',
        error
      );
    }
  }

  /**
   * Create session key from JWT token (one session per JWT)
   */
  createSessionKey(context: SlackNotificationContext): string {
    // Use the JWT token as the session key - this ensures one Slack thread per user session
    // regardless of how many TSPs/fleets they onboard
    if (context.token) {
      // Use a hash of the token to avoid storing the full JWT in session keys
      // Just take the last 10 characters for uniqueness while keeping it short
      return `jwt-${context.token.slice(-10)}`;
    }

    // Fallback to serviceId-fleetCode for cases where token isn't available
    const serviceId = context.serviceId || 'unknown';
    const fleetCode = context.fleetCode || 'unknown';
    return `${serviceId}-${fleetCode}`;
  }

  /**
   * Create attempt key for tracking multiple TSP attempts within a session
   */
  createAttemptKey(context: SlackNotificationContext): string {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);
    const attemptNumber = session?.currentAttempt || 1;
    return `${sessionKey}-attempt-${attemptNumber}`;
  }

  /**
   * Check if this is a new session that needs onboarding started message
   */
  shouldStartSession(context: SlackNotificationContext): boolean {
    const sessionKey = this.createSessionKey(context);

    // Check if session already exists
    if (this.sessions.has(sessionKey)) {
      // If the session is old (more than 1 hour), allow a new session
      const session = this.sessions.get(sessionKey)!;
      const sessionAge = Date.now() - session.startedAt;
      const oneHour = 60 * 60 * 1000;

      if (sessionAge > oneHour) {
        // Remove old session
        this.sessions.delete(sessionKey);
        this.saveToStorage();
        return true;
      }

      return false;
    }

    // Check if we're already in the process of starting this session
    if (this.pendingStarts.has(sessionKey)) {
      return false;
    }

    return true;
  }

  /**
   * Check if we should start a new attempt within an existing session
   * For JWT-based sessions, we rarely need attempt separators - just track everything chronologically
   */
  shouldStartNewAttempt(
    context: SlackNotificationContext,
    eventType: string
  ): boolean {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    if (!session) {
      return false; // No session exists
    }

    // For JWT-based continuous sessions, only create attempt separators for actual retries
    // of the same TSP after it was already completed or failed
    if (context.tsp && eventType === 'tsp_selected') {
      // Check if this TSP was already completed - if so, this is a retry
      if (session.completedTSPs.has(context.tsp)) {
        return true; // This is a retry of a completed TSP
      }

      // Check if this TSP was already attempted (has credential activity) - if so, this is a retry
      const hasCredentialActivityForThisTsp = context.tsp
        ? Array.from(session.activities).some(
            (activity) =>
              activity.includes('credentials_entered') &&
              activity.includes(context.tsp!)
          )
        : false;

      if (hasCredentialActivityForThisTsp) {
        return true; // This is a retry of a previously attempted TSP
      }
    }

    return false; // No attempt separator needed - just continue chronologically
  }

  /**
   * Start a new attempt within existing session
   */
  startNewAttempt(context: SlackNotificationContext): void {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    if (session) {
      session.currentAttempt += 1;
      session.activities.clear(); // Clear activities for new attempt
      session.lastActivity = '';
      session.isCompleted = false;
      this.saveToStorage();
    }
  }

  /**
   * Mark session as being started (prevents duplicates during async operations)
   */
  markSessionStarting(context: SlackNotificationContext): void {
    const sessionKey = this.createSessionKey(context);
    this.pendingStarts.add(sessionKey);
    this.saveToStorage();
  }

  /**
   * Start a new session
   */
  startSession(context: SlackNotificationContext, messageId?: string): void {
    const sessionKey = this.createSessionKey(context);

    // Remove from pending starts
    this.pendingStarts.delete(sessionKey);

    this.sessions.set(sessionKey, {
      messageId,
      lastActivity: '',
      activities: new Set(),
      startedAt: Date.now(),
      currentAttempt: 1,
      completedTSPs: new Set(),
      isCompleted: false,
    });

    this.saveToStorage();
  }

  /**
   * Mark TSP as completed in session
   */
  markTSPCompleted(context: SlackNotificationContext, tsp: string): void {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    if (session && tsp) {
      session.completedTSPs.add(tsp);
      // Don't mark session as completed - allow multiple TSPs in same session
      session.isCompleted = false;
      this.saveToStorage();
    }
  }

  /**
   * Mark session as ended (quit/skipped)
   */
  markSessionEnded(context: SlackNotificationContext): void {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    if (session) {
      session.isCompleted = true;
      this.saveToStorage();
    }
  }

  /**
   * Check if activity should be tracked (not duplicate)
   */
  shouldTrackActivity(
    context: SlackNotificationContext,
    activity: string
  ): boolean {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    // If session doesn't exist, create one automatically for JWT-based flows
    if (!session) {
      // For OAuth callback scenarios (onboarding_failed, onboarding_completed),
      // allow tracking even without existing session - this handles cases where
      // session was lost during OAuth redirect
      if (
        activity.includes('onboarding_failed') ||
        activity.includes('onboarding_completed')
      ) {
        return true;
      }

      // If session is pending, allow tracking
      if (this.pendingStarts.has(sessionKey)) {
        return true;
      }

      // For JWT-based flows, auto-create session if it doesn't exist
      // This handles cases where users navigate directly to pages without going through Approve
      if (context.serviceId && context.fleetCode) {
        console.log(
          '[Slack] Auto-creating session for JWT-based flow:',
          sessionKey
        );
        this.startSession(context);
        return true;
      }

      return false;
    }

    // Always allow tracking for JWT-based sessions - never consider them "ended"
    // Sessions stay open for the entire JWT lifetime

    // Only prevent immediate duplicates (same activity as the very last one)
    // This prevents double-clicks but allows legitimate re-selections after other activities
    if (session.lastActivity === activity) {
      return false;
    }

    return true;
  }

  /**
   * Track an activity for the session
   */
  trackActivity(context: SlackNotificationContext, activity: string): void {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);

    if (session) {
      session.lastActivity = activity;
      session.activities.add(activity);
      this.saveToStorage();
    }
  }

  /**
   * Get message ID for threading
   */
  getMessageId(context: SlackNotificationContext): string | undefined {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);
    return session?.messageId;
  }

  /**
   * Set message ID for a session
   */
  setMessageId(context: SlackNotificationContext, messageId: string): void {
    const sessionKey = this.createSessionKey(context);
    const session = this.sessions.get(sessionKey);
    if (session) {
      session.messageId = messageId;
      this.saveToStorage();
    }
  }

  /**
   * Check if a session is pending (being started)
   */
  isPending(sessionKey: string): boolean {
    return this.pendingStarts.has(sessionKey);
  }

  /**
   * Get session data for a context
   */
  getSession(context: SlackNotificationContext) {
    const sessionKey = this.createSessionKey(context);
    return this.sessions.get(sessionKey);
  }

  /**
   * Clean up old sessions (call periodically)
   */
  cleanup(): void {
    // Clean up old pending starts (older than 30 seconds)
    const pendingArray = Array.from(this.pendingStarts);
    if (pendingArray.length > 10) {
      this.pendingStarts.clear();
    }

    // Keep only last 100 sessions to prevent memory leaks
    if (this.sessions.size > 100) {
      const entries = Array.from(this.sessions.entries());
      const toKeep = entries.slice(-50); // Keep last 50
      this.sessions.clear();
      toKeep.forEach(([key, value]) => this.sessions.set(key, value));
    }
  }
}

// Global session tracker instance
const sessionTracker = new SessionTracker();

/**
 * Create user identifier for display
 */
function createUserIdentifier(context: SlackNotificationContext): string {
  // For JWT-based sessions, show service info
  if (context.serviceId && context.fleetCode) {
    return `Service ${context.serviceId} (${context.fleetCode})`;
  }
  if (context.fleetCode) {
    return `Fleet: ${context.fleetCode}`;
  }
  if (context.serviceId) {
    return `Service: ${context.serviceId}`;
  }
  return 'Unknown User';
}

/**
 * Create service identifier for display
 */
function createServiceIdentifier(context: SlackNotificationContext): string {
  if (context.serviceName) {
    return context.serviceName;
  }
  if (context.serviceId) {
    return `Service ${context.serviceId}`;
  }
  return 'Unknown Service';
}

/**
 * Map SlackEventType to action string for structured data
 */
function getActionFromEventType(eventType: SlackEventType): string {
  switch (eventType) {
    case SlackEventType.ONBOARDING_STARTED:
      return 'onboarding_started';
    case SlackEventType.ONBOARDING_COMPLETED:
      return 'onboarding_completed';
    case SlackEventType.ONBOARDING_FAILED:
      return 'onboarding_failed';
    case SlackEventType.ONBOARDING_SKIPPED:
      return 'onboarding_skipped';
    case SlackEventType.TSP_SELECTED:
      return 'tsp_selected';
    case SlackEventType.TSP_REMOVED:
      return 'tsp_removed';
    case SlackEventType.CREDENTIALS_ENTERED:
      return 'credentials_entered';
    case SlackEventType.ERROR_ENCOUNTERED:
      return 'error_encountered';
    case SlackEventType.HELP_REQUESTED:
      return 'help_requested';
    case SlackEventType.PAGE_VIEW:
      return 'page_view';
    case SlackEventType.SKIP_INITIATED:
      return 'skip_initiated';
    case SlackEventType.RETRY_ATTEMPTED:
      return 'retry_attempted';
    case SlackEventType.BACK_NAVIGATION:
      return 'back_navigation';
    case SlackEventType.TERMS_CLICKED:
      return 'terms_clicked';
    case SlackEventType.PRIVACY_CLICKED:
      return 'privacy_clicked';
    default:
      return String(eventType);
  }
}

/**
 * Determine page name from event type and details
 */
function getPageFromEventType(
  eventType: SlackEventType,
  details?: Record<string, any>
): string {
  // If page is explicitly provided in details, use it
  if (details?.page) {
    return details.page;
  }

  // Infer page from event type
  switch (eventType) {
    case SlackEventType.ONBOARDING_STARTED:
      return 'approve';
    case SlackEventType.TSP_SELECTED:
      return 'tsp_selection';
    case SlackEventType.TSP_REMOVED:
      return 'update_permissions';
    case SlackEventType.CREDENTIALS_ENTERED:
      return 'credentials_form';
    case SlackEventType.ONBOARDING_COMPLETED:
      return 'success';
    case SlackEventType.ONBOARDING_FAILED:
      return 'error';
    case SlackEventType.ONBOARDING_SKIPPED:
      return 'skip_completed';
    case SlackEventType.TERMS_CLICKED:
      return 'terms_clicked';
    case SlackEventType.PRIVACY_CLICKED:
      return 'privacy_clicked';
    default:
      return 'unknown';
  }
}

/**
 * Send notification to backend with structured data for fleet onboarding logs
 */
async function sendToBackend(
  message: string,
  context?: SlackNotificationContext,
  eventType?: SlackEventType,
  details?: Record<string, any>,
  isThreaded: boolean = false,
  parentMessageId?: string
): Promise<string | undefined> {
  try {
    const axios = (await import('../axios')).default;
    const { tokenStorage } = await import('./tokenStorage');
    const config = getSlackConfig();

    // Check if we have a token
    const token = tokenStorage.get();

    // If no token is available, skip the notification
    if (!token) {
      console.warn('[Slack] No token available, skipping notification');
      return undefined;
    }

    const payload: any = {
      message,
      environment: config.environment,
      channel: config.channel,
    };

    if (isThreaded && parentMessageId) {
      payload.thread_ts = parentMessageId;
    }

    // Add structured data for fleet onboarding logs
    if (context) {
      const structuredData: Record<string, any> = {
        service_id: context.serviceId,
        fleet_code: context.fleetCode,
        timestamp: context.timestamp || new Date().toISOString(),
      };

      // Add TSP information if available
      if (context.tsp) {
        structuredData.tsp_name = context.tsp;
      }

      // Add event-specific data based on event type
      if (eventType) {
        structuredData.action = getActionFromEventType(eventType);
        structuredData.page_name = getPageFromEventType(eventType, details);
      }

      // Add additional details if provided
      if (details) {
        // Map common detail fields
        if (details.errorType) structuredData.error_type = details.errorType;
        if (details.errorMessage)
          structuredData.error_msg = details.errorMessage;
        if (details.reason) structuredData.error_msg = details.reason;
        if (details.helpType) structuredData.help_type = details.helpType;
        if (details.attemptNumber)
          structuredData.attempt_num = details.attemptNumber;
        if (details.page) structuredData.page_name = details.page;
        if (details.credentialType)
          structuredData.credential_type = details.credentialType;
        if (details.isUpdate !== undefined)
          structuredData.is_update = details.isUpdate;
        if (details.tspName) structuredData.tsp_display_name = details.tspName;
      }

      payload.structured_data = structuredData;
    }

    // The axios instance should already have the Authorization header configured
    // from the JWT context, but let's make sure it's included
    const response = await axios.post('/slack/notify', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      // Backend returns message_ts for threading (Web API) or undefined for webhooks
      const messageId =
        response.data?.message_ts ||
        response.data?.ts ||
        response.data?.message_id;
      return messageId;
    }

    console.warn('[Slack] Unexpected response status:', response.status);
    return undefined;
  } catch (error: any) {
    // Enhanced error logging
    console.warn('[Slack] Error sending to backend:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
    });
    return undefined;
  }
}

/**
 * Track onboarding started (creates new message thread)
 */
async function trackOnboardingStarted(
  context: SlackNotificationContext,
  jwtState?: any
): Promise<void> {
  if (!sessionTracker.shouldStartSession(context)) {
    return; // Session already started or starting
  }

  // Mark as starting to prevent duplicates
  sessionTracker.markSessionStarting(context);

  const userIdentifier = createUserIdentifier(context);
  const serviceIdentifier = createServiceIdentifier(context);
  const config = getSlackConfig();

  const message = `🚀 **Onboarding Session Started** - ${serviceIdentifier} (${userIdentifier}) [${config.environment}]`;

  const messageId = await sendToBackend(
    message,
    context,
    undefined,
    undefined,
    false
  );

  sessionTracker.startSession(context, messageId);

  // Send JWT context as first thread for debugging
  if (messageId && jwtState) {
    const jwtContextMessage = createJWTContextMessage(jwtState);
    await sendToBackend(
      jwtContextMessage,
      context,
      undefined,
      undefined,
      true,
      messageId
    );
  }
}

/**
 * Track activity in existing session (threaded reply)
 */
async function trackActivity(
  context: SlackNotificationContext,
  eventType: SlackEventType,
  details?: Record<string, any>
): Promise<void> {
  // Check if we should start a new attempt within existing session
  // Only do this for TSP selection events to avoid creating attempts for navigation
  if (
    eventType === SlackEventType.TSP_SELECTED &&
    sessionTracker.shouldStartNewAttempt(context, eventType as string)
  ) {
    sessionTracker.startNewAttempt(context);

    // Send attempt separator message
    const session = sessionTracker.getSession(context);
    const attemptNumber = session?.currentAttempt || 1;

    const parentMessageId = sessionTracker.getMessageId(context);
    if (parentMessageId) {
      const attemptMessage = `🔄 **--- Attempt #${attemptNumber} ---**`;
      await sendToBackend(
        attemptMessage,
        context,
        undefined,
        undefined,
        true,
        parentMessageId
      );
    }
  }

  // Create activity identifier for deduplication
  let activityId = eventType as string;
  if (details?.page) activityId += `-${details.page}`;
  if (details?.tsp) activityId += `-${details.tsp}`;
  if (details?.errorType) activityId += `-${details.errorType}`;

  if (!sessionTracker.shouldTrackActivity(context, activityId)) {
    console.warn(
      '[Slack] shouldTrackActivity returned false - duplicate or no session'
    );
    return; // Duplicate or no session
  }

  const parentMessageId = sessionTracker.getMessageId(context);

  if (!parentMessageId) {
    // For OAuth callback events (onboarding_failed, onboarding_completed),
    // create a new session if none exists
    if (
      activityId.includes('onboarding_failed') ||
      activityId.includes('onboarding_completed')
    ) {
      // Start a new session for this OAuth callback
      await trackOnboardingStarted(context);

      // Get the new parent message ID
      const newParentMessageId = sessionTracker.getMessageId(context);
      if (newParentMessageId) {
        const message = createActivityMessage(eventType, context, details);
        await sendToBackend(
          message,
          context,
          eventType,
          details,
          true,
          newParentMessageId
        );
        sessionTracker.trackActivity(context, activityId);

        // Handle session state changes
        if (eventType === SlackEventType.ONBOARDING_COMPLETED && context.tsp) {
          sessionTracker.markTSPCompleted(context, context.tsp);
          // Never end session - keep it open for the entire JWT lifetime
        }
        // Never end session for any activity - let it stay open for entire JWT session
        return;
      }
    }

    console.warn(
      '[Slack] No parent message to thread to, skipping activity:',
      eventType
    );
    return; // No parent message to thread to
  }

  // Create activity message
  const message = createActivityMessage(eventType, context, details);

  await sendToBackend(
    message,
    context,
    eventType,
    details,
    true,
    parentMessageId
  );
  sessionTracker.trackActivity(context, activityId);

  // Handle session state changes
  if (eventType === SlackEventType.ONBOARDING_COMPLETED && context.tsp) {
    sessionTracker.markTSPCompleted(context, context.tsp);
    // Never end session - keep it open for the entire JWT lifetime
  }
  // Never end session for any activity - let it stay open for entire JWT session
  // This allows continuous tracking of all user activities within the same thread
}

/**
 * Create JWT context message for debugging
 */
function createJWTContextMessage(jwtState: any): string {
  const timestamp = new Date().toLocaleTimeString();

  // Extract relevant JWT information for debugging
  const jwtInfo = {
    serviceId: jwtState.serviceId || 'null',
    fleetCode: jwtState.fleetCode || 'null',
    redirectURISuccess: jwtState.redirectURISuccess || 'null',
    redirectURIError: jwtState.redirectURIError || 'null',
    isValid: jwtState.isValid || false,
    isExpired: jwtState.isExpired || false,
    errorType: jwtState.errorType || 'null',
    errorMessage: jwtState.errorMessage || 'null',
    canRedirectToService: jwtState.canRedirectToService || false,
    preferredRedirectURI: jwtState.preferredRedirectURI || 'null',
  };

  // Format as code block for easy reading
  const codeBlock = '```json\n' + JSON.stringify(jwtInfo, null, 2) + '\n```';

  return `🔍 ${timestamp} - **JWT Context**\n${codeBlock}`;
}

/**
 * Create formatted activity message
 */
function createActivityMessage(
  eventType: SlackEventType,
  context: SlackNotificationContext,
  details?: Record<string, any>
): string {
  const timestamp = new Date().toLocaleTimeString();

  switch (eventType) {
    case SlackEventType.PAGE_VIEW:
      return `👀 ${timestamp} - Viewed: **${details?.page || 'Unknown Page'}**`;

    case SlackEventType.TSP_SELECTED:
      return `🎯 ${timestamp} - Selected TSP: **${
        details?.tspName || context.tsp || 'Unknown'
      }**`;

    case SlackEventType.TSP_REMOVED:
      return `🗑️ ${timestamp} - Removed **${
        context.tsp || 'Unknown TSP'
      }** integration`;

    case SlackEventType.CREDENTIALS_ENTERED:
      const updateText = details?.isUpdate ? ' (updating existing)' : '';
      return `🔑 ${timestamp} - Entered credentials for **${
        context.tsp || 'Unknown TSP'
      }**${updateText}`;

    case SlackEventType.ERROR_ENCOUNTERED:
      const tspInfo = context.tsp ? ` with **${context.tsp}**` : '';
      return `⚠️ ${timestamp} - Error on ${
        details?.page || 'unknown page'
      }${tspInfo}: **${details?.errorType || 'Unknown'}** - ${
        details?.errorMessage || 'No details'
      }`;

    case SlackEventType.HELP_REQUESTED:
      return `🆘 ${timestamp} - Requested help: **${
        details?.helpType || 'General'
      }**${details?.page ? ` on ${details.page}` : ''}`;

    case SlackEventType.SKIP_INITIATED:
      return `⏸️ ${timestamp} - Initiated skip from **${
        details?.page || 'Unknown Page'
      }**`;

    case SlackEventType.RETRY_ATTEMPTED:
      const attemptText = details?.attemptNumber
        ? ` (attempt #${details.attemptNumber})`
        : '';
      return `🔄 ${timestamp} - Retry attempted for **${
        context.tsp || 'Unknown TSP'
      }**${attemptText}`;

    case SlackEventType.BACK_NAVIGATION:
      const fromTsp = details?.fromTsp ? ` from **${details.fromTsp}**` : '';
      return `⬅️ ${timestamp} - Navigated back to TSP selection${fromTsp}`;

    case SlackEventType.ONBOARDING_COMPLETED:
      return `✅ ${timestamp} - **COMPLETED** onboarding with **${
        context.tsp || 'Unknown TSP'
      }**`;

    case SlackEventType.ONBOARDING_SKIPPED:
      const reason = details?.reason ? ` - ${details.reason}` : '';
      return `⏭️ ${timestamp} - **SKIPPED** onboarding${reason}`;

    case SlackEventType.ONBOARDING_FAILED:
      return `❌ ${timestamp} - **FAILED** onboarding with **${
        context.tsp || 'Unknown TSP'
      }** - ${details?.reason || 'Unknown reason'}`;

    case SlackEventType.TERMS_CLICKED:
      return `🔖 ${timestamp} - Terms clicked`;

    case SlackEventType.PRIVACY_CLICKED:
      return `🔒 ${timestamp} - Privacy clicked`;

    default:
      return `📋 ${timestamp} - ${eventType}`;
  }
}

/**
 * Create Slack notification context from JWT state
 */
export function createSlackContext(
  jwt: JWTContextValue,
  serviceName?: string,
  additionalData?: { tsp?: string }
): SlackNotificationContext {
  return {
    serviceName: serviceName,
    serviceId: jwt.serviceId || undefined,
    fleetCode: jwt.fleetCode || undefined,
    token: jwt.rawToken || undefined,
    tsp: additionalData?.tsp || undefined,
    userId: jwt.serviceId ? `service-${jwt.serviceId}` : undefined,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Main tracking function
 */
export async function trackUserJourney(
  eventType: SlackEventType,
  context: SlackNotificationContext,
  additionalData?: Record<string, any>
): Promise<void> {
  try {
    // Clean up old sessions periodically
    sessionTracker.cleanup();

    if (eventType === SlackEventType.ONBOARDING_STARTED) {
      await trackOnboardingStarted(context, additionalData?.jwtState);
    } else {
      await trackActivity(context, eventType, additionalData);
    }
  } catch (error) {
    // Enhanced error logging instead of silent failure
    console.error('[Slack] trackUserJourney error:', error);
  }
}

/**
 * Convenience functions for common events
 */
export const SlackTracker = {
  /**
   * Track when user starts onboarding (creates new message thread)
   */
  onboardingStarted: (context: SlackNotificationContext, jwtState?: any) =>
    trackUserJourney(SlackEventType.ONBOARDING_STARTED, context, { jwtState }),

  /**
   * Track when user completes onboarding
   */
  onboardingCompleted: (context: SlackNotificationContext, tsp: string) =>
    trackUserJourney(SlackEventType.ONBOARDING_COMPLETED, { ...context, tsp }),

  /**
   * Track when user skips onboarding (but keep session open)
   */
  onboardingSkipped: (context: SlackNotificationContext, reason?: string) =>
    trackUserJourney(SlackEventType.ONBOARDING_SKIPPED, context, { reason }),

  /**
   * Track when onboarding fails
   */
  onboardingFailed: (
    context: SlackNotificationContext,
    reason: string,
    tsp?: string
  ) =>
    trackUserJourney(
      SlackEventType.ONBOARDING_FAILED,
      { ...context, tsp },
      { reason }
    ),

  /**
   * Track page views
   */
  pageView: (context: SlackNotificationContext, page: string) =>
    trackUserJourney(SlackEventType.PAGE_VIEW, context, { page }),

  /**
   * Track TSP selection
   */
  tspSelected: (
    context: SlackNotificationContext,
    tsp: string,
    tspName: string
  ) =>
    trackUserJourney(
      SlackEventType.TSP_SELECTED,
      { ...context, tsp },
      { tspName }
    ),

  /**
   * Track TSP removal
   */
  tspRemoved: (
    context: SlackNotificationContext,
    tsp: string,
    tspName?: string
  ) =>
    trackUserJourney(
      SlackEventType.TSP_REMOVED,
      { ...context, tsp },
      { tspName }
    ),

  /**
   * Track credential entry
   */
  credentialsEntered: (
    context: SlackNotificationContext,
    tsp: string,
    credentialType: string,
    isUpdate = false
  ) =>
    trackUserJourney(
      SlackEventType.CREDENTIALS_ENTERED,
      { ...context, tsp },
      { credentialType, isUpdate }
    ),

  /**
   * Track errors
   */
  errorEncountered: (
    context: SlackNotificationContext,
    errorType: string,
    errorMessage: string,
    page?: string
  ) =>
    trackUserJourney(SlackEventType.ERROR_ENCOUNTERED, context, {
      errorType,
      errorMessage,
      page,
    }),

  /**
   * Track help requests
   */
  helpRequested: (
    context: SlackNotificationContext,
    helpType: string,
    page?: string
  ) =>
    trackUserJourney(SlackEventType.HELP_REQUESTED, context, {
      helpType,
      page,
    }),

  /**
   * Track skip attempts
   */
  skipInitiated: (context: SlackNotificationContext, page: string) =>
    trackUserJourney(SlackEventType.SKIP_INITIATED, context, { page }),

  /**
   * Track retry attempts
   */
  retryAttempted: (
    context: SlackNotificationContext,
    tsp: string,
    attemptNumber?: number
  ) =>
    trackUserJourney(
      SlackEventType.RETRY_ATTEMPTED,
      { ...context, tsp },
      { attemptNumber }
    ),

  /**
   * Track back navigation
   */
  backNavigation: (context: SlackNotificationContext, fromTsp?: string) =>
    trackUserJourney(SlackEventType.BACK_NAVIGATION, context, { fromTsp }),

  /**
   * Track terms clicked
   */
  termsClicked: (context: SlackNotificationContext) =>
    trackUserJourney(SlackEventType.TERMS_CLICKED, context, {}),

  /**
   * Track privacy clicked
   */
  privacyClicked: (context: SlackNotificationContext) =>
    trackUserJourney(SlackEventType.PRIVACY_CLICKED, context, {}),
};
